// AdminGrowthQueuePage.tsx — Phase 0.5 UI: the operator's home screen. One view of everything in
// flight across arcs, grouped by what needs the operator vs what's still working (GAP O1/O2).
//   - awaiting-me badge (the core daily signal)
//   - buckets: awaiting_review (actionable) / rendering / failed / done, actionable-first
//   - failed items carry a recovery action (retry / edit-&-regenerate / abandon) (O3)
//   - non-terminal items flag "taking too long" (O4)
//   - each row links into the RIGHT review surface for its kind (still/carousel → spread review;
//     video → storyboard) — the leave-and-return mechanism (O6): come back here, act on what's ready.
//   - completed rows expose the final asset (retrieval, O5/4.8) via the run status renderUrl.
//
// Polls the queue so newly-ready items surface without a manual refresh.
// Reachable at /admin/growth/queue. Inline-token styling (matches the other growth pages).
import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { growthQueueApi, type QueueView, type QueueItem } from "../../api/growth.ts";

const wrap: React.CSSProperties = { padding: 16, maxWidth: 1100, margin: "0 auto" };
const card: React.CSSProperties = { border: "1px solid #e5e7eb", borderRadius: 10, padding: 12, background: "#fff", marginBottom: 8 };
const btn: React.CSSProperties = { padding: "5px 10px", borderRadius: 7, border: "1px solid #d1d5db", background: "#fff", cursor: "pointer", fontSize: 12, textDecoration: "none", color: "#111827", display: "inline-block" };
const btnPrimary: React.CSSProperties = { ...btn, background: "#2f855a", color: "#fff", border: 0 };
const muted: React.CSSProperties = { fontSize: 12, color: "#6b7280" };
const pill = (bg: string, fg: string): React.CSSProperties => ({ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 999, background: bg, color: fg });

const POLL_MS = 5000;

const BUCKET_META: Record<QueueItem["bucket"], { label: string; bg: string; fg: string }> = {
  awaiting_review: { label: "Awaiting you", bg: "#fef3c7", fg: "#92400e" },
  rendering:       { label: "Rendering",    bg: "#dbeafe", fg: "#1e40af" },
  failed:          { label: "Failed",       bg: "#fee2e2", fg: "#991b1b" },
  done:            { label: "Done",         bg: "#dcfce7", fg: "#166534" },
};

const KIND_META: Record<QueueItem["kind"], string> = { still: "Image", carousel: "Carousel", video: "Video" };

/** The review surface a row links to, by kind. still/carousel → the spread review; video → storyboard. */
function reviewLinkFor(it: QueueItem): string {
  if (it.kind === "video") return `/admin/growth/arcs/${it.arcId}`;         // storyboard drill-down
  return `/admin/growth/arcs/${it.arcId}/review/${it.kind}`;                // still | carousel spread review
}

function RecoveryAction(props: { it: QueueItem }) {
  const { it } = props;
  const to = reviewLinkFor(it);
  switch (it.recoveryAction) {
    case "retry":           return <Link style={btn} to={to}>Retry</Link>;
    case "edit_regenerate": return <Link style={btn} to={to}>Edit inputs & regenerate</Link>;
    case "abandon":         return <span style={muted}>Abandon (no retry — see reason)</span>;
    default:                return <Link style={btn} to={to}>Open</Link>;
  }
}

/** The item's picture — the rendered video, the still, the carousel's first slide — or a placeholder the same size. */
function Thumb({ it }: { it: QueueItem }) {
  const box = { width: "100%", aspectRatio: "3 / 4", borderRadius: 6, background: "#f3f4f6", objectFit: "cover" as const, display: "block" };
  if (it.picture?.kind === "image") return <img src={it.picture.url} alt={`${it.kind} preview`} style={box} loading="lazy" data-visual />;
  if (it.picture?.kind === "video") return <video src={`${it.picture.url}#t=1`} muted preload="metadata" style={box} data-visual />;
  return <div style={{ ...box, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af", fontSize: 11 }} data-visual>
    {it.bucket === "rendering" ? "rendering…" : "no picture"}</div>;
}

/** Operator visuals G7: one queue item as a PICTURE card — the thing first, its status as small marks beneath. */
function QueueRow(props: { it: QueueItem }) {
  const { it } = props;
  const meta = BUCKET_META[it.bucket];
  return (
    <div style={{ ...card, width: 180, padding: 8, display: "flex", flexDirection: "column", gap: 6 }}>
      <Link to={reviewLinkFor(it)} title={`${KIND_META[it.kind]} · ${it.phase ?? it.bucket}`}><Thumb it={it} /></Link>
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
        <span style={pill(meta.bg, meta.fg)}>{meta.label}</span>
        <span style={{ fontSize: 11, fontWeight: 600 }}>{KIND_META[it.kind]}</span>
        {it.takingTooLong && <span style={pill("#fef3c7", "#92400e")} title="taking longer than usual">⏱</span>}
        {!it.reachable && <span style={pill("#f3f4f6", "#6b7280")} title="status unavailable">?</span>}
      </div>
      {it.bucket === "failed" && <RecoveryAction it={it} />}
      {it.bucket === "awaiting_review" && <Link style={{ ...btnPrimary, textAlign: "center" }} to={reviewLinkFor(it)}>Review</Link>}
      {/* the details on hover — the correlation id for support/triage (D7), the phase, the gate, the failure */}
      <span style={{ ...muted, fontSize: 10, fontFamily: "monospace" }}
        title={[`runId ${it.runId}`, it.phase ?? it.bucket, it.pct != null ? `${it.pct}%` : "", it.gate?.gateName ? `gate: ${it.gate.gateName}` : "",
          it.failure?.category ?? ""].filter(Boolean).join(" · ")}>{it.runId.slice(0, 8)}…</span>
    </div>
  );
}

export function AdminGrowthQueuePage() {
  const [view, setView] = useState<QueueView | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [bucket, setBucket] = useState<string>("");   // "" = all
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      const v = await growthQueueApi.queue(bucket ? { bucket } : undefined, signal);
      setView(v); setErr(null);
    } catch (e) {
      if (!(e instanceof DOMException && e.name === "AbortError")) setErr(e instanceof Error ? e.message : "failed to load queue");
    }
  }, [bucket]);

  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    pollRef.current = setInterval(() => load(), POLL_MS);
    return () => { ctrl.abort(); if (pollRef.current) clearInterval(pollRef.current); };
  }, [load]);

  const counts = view?.counts ?? {};
  const order: QueueItem["bucket"][] = ["awaiting_review", "rendering", "failed", "done"];

  return (
    <div style={wrap}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>Review queue</h2>
        {view && view.awaitingMe > 0 && (
          <span style={pill("#f59e0b", "#fff")}>{view.awaitingMe} awaiting you</span>
        )}
        <span style={{ flex: 1 }} />
        <span style={muted}>auto-refresh {POLL_MS / 1000}s</span>
      </div>

      {/* bucket filter tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
        <button style={bucket === "" ? btnPrimary : btn} onClick={() => setBucket("")}>
          All{view ? ` (${view.total})` : ""}
        </button>
        {order.map((b) => (
          <button key={b} style={bucket === b ? btnPrimary : btn} onClick={() => setBucket(b)}>
            {BUCKET_META[b].label}{counts[b] != null ? ` (${counts[b]})` : ""}
          </button>
        ))}
      </div>

      {err && <div style={{ ...card, borderColor: "#fca5a5", background: "#fef2f2", color: "#b91c1c" }}>{err}</div>}

      {view && view.items.length === 0 && (
        <div style={{ ...card, ...muted }}>
          Nothing {bucket ? BUCKET_META[bucket as QueueItem["bucket"]]?.label.toLowerCase() : "in flight"}.
          Generate candidates from an arc, then come back — ready items appear here.
        </div>
      )}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
        {view?.items.map((it) => <QueueRow key={it.runId} it={it} />)}
      </div>
    </div>
  );
}
